// src/components/divesitedashboard/PaginationControls.jsx
import React from "react";
import {
  Stack,
  Pagination,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
} from "@mui/material";

const PaginationControls = ({ page, totalPages, handlePageChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Responsive pagination size
  const getPaginationSize = () => {
    if (isMobile) return "small";
    if (isTablet) return "medium";
    return "large";
  };

  // Responsive pagination props
  const getPaginationProps = () => {
    const baseProps = {
      count: totalPages,
      page: page,
      onChange: handlePageChange,
      color: "primary",
      size: getPaginationSize(),
      "aria-label": "dive sites pagination",
    };

    if (isMobile) {
      return {
        ...baseProps,
        // Show fewer page numbers on mobile
        siblingCount: 0,
        boundaryCount: 1,
        // Hide prev/next text on very small screens
        showFirstButton: false,
        showLastButton: false,
      };
    }

    if (isTablet) {
      return {
        ...baseProps,
        siblingCount: 1,
        boundaryCount: 1,
        showFirstButton: true,
        showLastButton: true,
      };
    }

    return {
      ...baseProps,
      siblingCount: 1,
      boundaryCount: 2,
      showFirstButton: true,
      showLastButton: true,
    };
  };

  // Mobile layout stacks vertically, desktop keeps horizontal
  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          mt: 2,
          mb: 2,
          px: 1, // Side padding on mobile
        }}
      >
        <Pagination
          {...getPaginationProps()}
          sx={{
            "& .MuiPaginationItem-root": {
              minWidth: "36px",
              height: "36px",
              fontSize: "0.875rem",
              // Better touch targets
              margin: "0 2px",
            },
            "& .MuiPaginationItem-page.Mui-selected": {
              fontWeight: 600,
            },
          }}
        />
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            fontSize: "0.75rem",
            textAlign: "center",
            mt: 0.5,
          }}
        >
          Page {page} of {totalPages}
        </Typography>
      </Box>
    );
  }

  // Tablet and desktop layout
  return (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center"
      spacing={isTablet ? 1.5 : 2}
      sx={{
        mt: 3,
        mb: 2,
        px: isTablet ? 2 : 0,
      }}
    >
      <Pagination
        {...getPaginationProps()}
        sx={{
          "& .MuiPaginationItem-root": {
            minWidth: isTablet ? "32px" : "40px",
            height: isTablet ? "32px" : "40px",
            fontSize: isTablet ? "0.875rem" : "0.9375rem",
          },
          "& .MuiPaginationItem-page.Mui-selected": {
            fontWeight: 600,
          },
        }}
      />
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{
          fontSize: isTablet ? "0.8125rem" : "0.875rem",
          whiteSpace: "nowrap",
          minWidth: "fit-content",
        }}
      >
        Page {page} of {totalPages}
      </Typography>
    </Stack>
  );
};

export default PaginationControls;
